import axiosInstance from '../utils/axios';

/**
 * 관리자 계정 관련 API 서비스
 */
class AdminAccountService {
  /**
   * 관리자 목록을 조회합니다.
   *
   * @param {Object} params 조회 파라미터
   * @param {number} params.page 페이지 번호 (기본값: 1)
   * @param {number} params.limit 페이지 크기 (기본값: 10)
   * @returns {Promise<Object>} 관리자 목록 조회 결과
   */
  async getAdmins(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 10,
      });

      const response = await axiosInstance.get(`/admin/list?${queryParams}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data || {},
        };
      }

      return {
        success: false,
        message:
          response.data.message || "관리자 목록을 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "관리자 목록을 불러오는 중 오류가 발생했습니다.";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * 새로운 관리자를 생성합니다.
   *
   * @param {Object} adminData 관리자 데이터
   * @param {string} adminData.id 관리자 ID
   * @param {string} adminData.pw 관리자 비밀번호
   * @param {string} adminData.name 관리자 이름
   * @param {string} adminData.role 관리자 역할
   * @param {string} adminData.email 관리자 이메일
   * @returns {Promise<Object>} 관리자 생성 결과
   */
  async createAdmin(adminData) {
    try {
      const response = await axiosInstance.post("/admin", {
        admin: {
          id: adminData.id,
          pw: adminData.pw,
          name: adminData.name,
          role: adminData.role,
          email: adminData.email,
        },
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message:
            response.data.message || "관리자가 성공적으로 등록되었습니다.",
        };
      }

      return {
        success: false,
        message: response.data.message || "관리자 등록에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "관리자 등록 중 오류가 발생했습니다.";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * 관리자 정보를 수정합니다.
   *
   * @param {string} adminIdx 관리자 인덱스
   * @param {Object} adminData 관리자 데이터
   * @param {string} adminData.id 관리자 ID
   * @param {string} adminData.pw 관리자 비밀번호
   * @param {string} adminData.name 관리자 이름
   * @param {string} adminData.role 관리자 역할
   * @param {string} adminData.email 관리자 이메일
   * @returns {Promise<Object>} 관리자 수정 결과
   */
  async updateAdmin(adminIdx, adminData) {
    try {
      const response = await axiosInstance.put(`/admin/${adminIdx}`, {
        admin: {
          id: adminData.id,
          pw: adminData.pw,
          name: adminData.name,
          role: adminData.role,
          email: adminData.email,
        },
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message:
            response.data.message || "관리자 정보가 성공적으로 수정되었습니다.",
        };
      }

      return {
        success: false,
        message: response.data.message || "관리자 정보 수정에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "관리자 수정 중 오류가 발생했습니다.";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * 관리자를 삭제합니다.
   *
   * @param {Array<string>} adminIds 삭제할 관리자 ID 배열
   * @returns {Promise<Object>} 관리자 삭제 결과
   */
  async deleteAdmins(adminIds) {
    try {
      // 단일 ID인 경우 DELETE /admin/{id}, 다중 ID인 경우 DELETE 요청 본문에 IDs 전송
      let response;
      if (adminIds.length === 1) {
        response = await axiosInstance.delete(`/admin/${adminIds[0]}`);
      } else {
        // 다중 삭제는 본문에 ID 배열 전송
        response = await axiosInstance.delete("/admin/batch", {
          data: { ids: adminIds },
        });
      }

      if (response.data && response.data.success) {
        return {
          success: true,
          message:
            response.data.message || "관리자가 성공적으로 삭제되었습니다.",
        };
      }

      return {
        success: false,
        message: response.data.message || "관리자 삭제에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "관리자 삭제 중 오류가 발생했습니다.";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * 현재 로그인한 관리자의 프로필 정보를 조회합니다.
   *
   * @returns {Promise<Object>} 프로필 조회 결과
   */
  async getMyProfile() {
    try {
      const response = await axiosInstance.get("/admin/profile");

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data || {},
        };
      }

      return {
        success: false,
        message:
          response.data.message || "프로필 정보를 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "프로필 정보를 불러오는 중 오류가 발생했습니다.";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * 현재 로그인한 관리자의 비밀번호를 변경합니다.
   *
   * @param {string} currentPassword 현재 비밀번호
   * @param {string} newPassword 새 비밀번호
   * @returns {Promise<Object>} 비밀번호 변경 결과
   */
  async changeMyPassword(currentPassword, newPassword) {
    try {
      const response = await axiosInstance.put("/admin/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message:
            response.data.message || "비밀번호가 성공적으로 변경되었습니다.",
        };
      }

      return {
        success: false,
        message: response.data.message || "비밀번호 변경에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "비밀번호 변경 중 오류가 발생했습니다.";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * 관리자 계정 활성화/비활성화
   *
   * @param {string} adminIdx 관리자 인덱스
   * @param {boolean} isActive 활성화 여부
   * @returns {Promise<Object>} 상태 변경 결과
   */
  async setAdminStatus(adminIdx, isActive) {
    try {
      const response = await axiosInstance.put(`/admin/${adminIdx}/status`, {
        is_active: isActive,
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message:
            response.data.message ||
            (isActive
              ? "계정이 활성화되었습니다."
              : "계정이 비활성화되었습니다."),
        };
      }

      return {
        success: false,
        message: response.data.message || "계정 상태 변경에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "계정 상태 변경 중 오류가 발생했습니다.";
      return {
        success: false,
        message: errorMessage,
      };
    }
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const adminAccountService = new AdminAccountService();
export default adminAccountService;
